import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2, Lock, ArrowRight, CreditCard, FileText, CheckCircle2, Users, BadgeCheck,
} from "lucide-react";
import { parsePrice } from "@/components/site/B2BBookingForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[12px] font-semibold text-sand-700 uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

const inputCls = "h-11 rounded-xl bg-cream-50 border-cream-300 text-sand-800 placeholder:text-sand-400 focus-visible:ring-terra";

/** Jahres-Fortbildungsplan (Paket Groß/Klein): Festpreis pro Pflegedienst – für alle Mitarbeitenden. */
export default function JahresplanForm({ course, onDone }) {
  const [form, setForm] = useState({
    organisation: "", contact_first_name: "", contact_last_name: "",
    contact_email: "", contact_phone: "", street: "", zip_code: "", city: "", team_size: "",
  });
  const [payMethod, setPayMethod] = useState("rechnung");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const total = parsePrice(course.price);
  const teamSize = parseInt(form.team_size, 10) || 0;
  const perHead = teamSize > 0 ? total / teamSize : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings/jahresplan`, {
        package_id: course.id,
        origin_url: window.location.origin,
        ...form,
        team_size: teamSize,
        payment_method: payMethod,
      });
      if (payMethod === "online" && res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setSuccess(res.data);
      setSubmitting(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Buchung konnte nicht übermittelt werden – bitte prüfe die Angaben.");
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="jahresplan-success" className="px-7 pb-8 pt-6 text-center">
        <CheckCircle2 className="h-12 w-12 text-sage-dark mx-auto mb-4" strokeWidth={1.4} />
        <h3 className="font-display text-2xl font-normal tracking-tight text-sand-800">Jahresplan gebucht!</h3>
        <p className="text-sand-600 text-[14.5px] leading-relaxed mt-2">
          <strong className="text-sand-800">{course.title}</strong> für {form.organisation} ist reserviert –
          gültig für <strong className="text-sand-800">alle Mitarbeitenden, 12 Monate</strong>.
          Die Rechnung über <strong className="text-sand-800">{fmtEur(success.amount_total)}</strong> geht
          per E-Mail an {form.contact_email}.
        </p>
        <Button onClick={onDone} data-testid="jahresplan-success-close" className="mt-6 rounded-full bg-terra hover:bg-terra-dark text-white px-8">
          Alles klar
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="px-7 pb-7 pt-5 space-y-5" data-testid="jahresplan-form">
      <div className="rounded-xl bg-sage/10 border border-sage/40 p-4 flex items-start gap-3">
        <BadgeCheck className="h-5 w-5 text-sage-dark shrink-0 mt-0.5" strokeWidth={1.7} />
        <p className="text-[12.5px] text-sand-700 leading-relaxed">
          <strong className="text-sand-800">Ein Festpreis – das ganze Team:</strong>{" "}
          Der Jahresplan gilt 12 Monate für <strong className="text-sand-800">alle Mitarbeitenden</strong> deiner
          Einrichtung, egal wie viele. Keine Kosten pro Person.
        </p>
      </div>

      <Field id="jp-org" label="Einrichtung / Pflegedienst *">
        <Input id="jp-org" data-testid="jahresplan-organisation" className={inputCls} required minLength={2}
          placeholder="Pflegedienst Sonnenschein GmbH" value={form.organisation} onChange={set("organisation")} />
      </Field>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="jp-cf" label="Vorname Ansprechpartner *">
          <Input id="jp-cf" data-testid="jahresplan-contact-first" className={inputCls} required minLength={2}
            value={form.contact_first_name} onChange={set("contact_first_name")} />
        </Field>
        <Field id="jp-cl" label="Nachname *">
          <Input id="jp-cl" data-testid="jahresplan-contact-last" className={inputCls} required minLength={2}
            value={form.contact_last_name} onChange={set("contact_last_name")} />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="jp-ce" label="E-Mail (Rechnung) *">
          <Input id="jp-ce" data-testid="jahresplan-contact-email" className={inputCls} type="email" required
            value={form.contact_email} onChange={set("contact_email")} />
        </Field>
        <Field id="jp-cp" label="Telefon *">
          <Input id="jp-cp" data-testid="jahresplan-contact-phone" className={inputCls} type="tel" required minLength={5}
            value={form.contact_phone} onChange={set("contact_phone")} />
        </Field>
      </div>

      <Field id="jp-street" label="Straße &amp; Hausnummer (Rechnungsadresse) *">
        <Input id="jp-street" data-testid="jahresplan-street" className={inputCls} required minLength={3}
          value={form.street} onChange={set("street")} />
      </Field>

      <div className="grid grid-cols-[120px_1fr] gap-4">
        <Field id="jp-zip" label="PLZ *">
          <Input id="jp-zip" data-testid="jahresplan-zip" className={inputCls} required minLength={4} maxLength={10}
            inputMode="numeric" value={form.zip_code} onChange={set("zip_code")} />
        </Field>
        <Field id="jp-city" label="Ort *">
          <Input id="jp-city" data-testid="jahresplan-city" className={inputCls} required minLength={2}
            value={form.city} onChange={set("city")} />
        </Field>
      </div>

      <Field id="jp-team" label="Wie viele Mitarbeitende hat dein Team? *">
        <Input id="jp-team" data-testid="jahresplan-team-size" className={inputCls} type="number" required
          min={1} max={500} placeholder="z. B. 25" value={form.team_size} onChange={set("team_size")} />
      </Field>

      {/* Preisbox mit Pro-Kopf-Kalkulation */}
      <div className="rounded-xl bg-sand-800 text-cream-50 p-5" data-testid="jahresplan-price-summary">
        <div className="flex justify-between text-[13.5px]">
          <span>{course.title}</span>
          <span>{fmtEur(total)}</span>
        </div>
        <div className="flex justify-between font-semibold text-[16px] mt-3 pt-3 border-t border-white/15">
          <span>Gesamt / Jahr</span>
          <span data-testid="jahresplan-total">{fmtEur(total)}</span>
        </div>
        {perHead !== null && (
          <p className="text-[12.5px] text-peach-200 mt-2.5 inline-flex items-center gap-1.5" data-testid="jahresplan-per-head">
            <Users className="h-3.5 w-3.5" strokeWidth={2} />
            Bei {teamSize} Mitarbeitenden nur {fmtEur(perHead)} pro Kopf – alle Pflichtfortbildungen inklusive.
          </p>
        )}
      </div>

      {/* Zahlungsart */}
      <div className="space-y-1.5">
        <Label className="text-[12px] font-semibold text-sand-700 uppercase tracking-wider">Zahlungsart *</Label>
        <div className="grid sm:grid-cols-2 gap-3">
          <button type="button" data-testid="jahresplan-pay-invoice" onClick={() => setPayMethod("rechnung")}
            className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${payMethod === "rechnung" ? "border-terra bg-terra/5" : "border-cream-300 bg-cream-50 hover:border-sand-400"}`}>
            <FileText className={`h-5 w-5 mt-0.5 shrink-0 ${payMethod === "rechnung" ? "text-terra" : "text-sand-500"}`} strokeWidth={1.7} />
            <span>
              <span className="block text-[13.5px] font-semibold text-sand-800">Auf Rechnung</span>
              <span className="block text-[12px] text-sand-600 mt-0.5">Rechnung per E-Mail · Überweisung</span>
            </span>
          </button>
          <button type="button" data-testid="jahresplan-pay-online" onClick={() => setPayMethod("online")}
            className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${payMethod === "online" ? "border-terra bg-terra/5" : "border-cream-300 bg-cream-50 hover:border-sand-400"}`}>
            <CreditCard className={`h-5 w-5 mt-0.5 shrink-0 ${payMethod === "online" ? "text-terra" : "text-sand-500"}`} strokeWidth={1.7} />
            <span>
              <span className="block text-[13.5px] font-semibold text-sand-800">Online bezahlen</span>
              <span className="block text-[12px] text-sand-600 mt-0.5">Karte oder Klarna</span>
            </span>
          </button>
        </div>
      </div>

      <Button type="submit" disabled={submitting} data-testid="jahresplan-submit"
        className="w-full rounded-full bg-terra hover:bg-terra-dark text-white py-3.5 text-base font-semibold group disabled:opacity-50">
        {submitting ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {payMethod === "rechnung" ? "Buchung wird übermittelt …" : "Weiterleitung zu Stripe …"}</>
        ) : (
          <>{payMethod === "rechnung" ? "Jahresplan zahlungspflichtig buchen" : "Weiter zur Zahlung"} ({fmtEur(total)})
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} /></>
        )}
      </Button>

      <p className="flex items-start gap-2 text-[11.5px] text-sand-500 leading-relaxed">
        <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={1.6} />
        <span>
          Mit der Buchung akzeptieren Sie unsere{" "}
          <Link to="/agb" className="underline underline-offset-2 hover:text-sand-800">AGB</Link> und{" "}
          <Link to="/datenschutz" className="underline underline-offset-2 hover:text-sand-800">Datenschutzerklärung</Link>.
        </span>
      </p>
    </form>
  );
}
